// Disponibilidade e "tempo útil decorrido" por PESSOA — espelha em TS a mesma
// regra de supabase/migrations/20260825120000_escala_comercial_interno.sql
// (fn_esta_disponivel). Usado por: distribuição de leads (o card de lead nunca
// aberto), tela de escala (preview "disponível agora?") e o cálculo de SLA
// "dentro do expediente" no Analytics Comercial.
//
// Deliberadamente um módulo novo, não uma extensão de src/lib/slaUtils.ts:
// aquele arquivo calcula uma janela ÚNICA pra empresa toda (só exclui fim de
// semana, sem almoço/ausência) pro SLA de Análise Cadastral — um uso
// incompatível com "por pessoa, com almoço variável e ausências".
//
// Mesmo deslocamento de fuso (-3h) que slaUtils.ts usa pra tratar timestamps
// como BRT sem depender de Intl/timezone do ambiente onde o código roda.

import type { HorarioTrabalho, AusenciaTrabalho } from '../../types';

const BRT_OFFSET_MS = 3 * 3600 * 1000;
const MINUTO_MS = 60 * 1000;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Minutos desde meia-noite de um horário 'HH:MM' ou 'HH:MM:SS'.
 *  Exportado porque a pausa de SLA (src/lib/pausaSla.ts) compara os mesmos
 *  campos hora_inicio/hora_fim de ausencias_trabalho. */
export const horaParaMinutos = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + (m || 0);
};

interface InstanteLocal {
    dow: number;      // 0=domingo .. 6=sábado
    minutos: number;  // minutos desde meia-noite, no fuso BRT
    dataISO: string;  // 'YYYY-MM-DD' no fuso BRT
}

/** Converte um Date (instante real) pra dia da semana + minuto do dia + data, já em BRT.
 *  Exportado para a pausa de SLA casar "agora" com a linha de ausência do dia
 *  usando exatamente esta convenção de fuso — e não a do navegador. */
export const paraLocalBRT = (instante: Date): InstanteLocal => {
    const local = new Date(instante.getTime() - BRT_OFFSET_MS);
    return {
        dow: local.getUTCDay(),
        minutos: local.getUTCHours() * 60 + local.getUTCMinutes(),
        dataISO: `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}`,
    };
};

/** true se `data` (ISO) tem uma ausência de dia inteiro cadastrada. */
const temAusenciaDiaInteiro = (ausencias: AusenciaTrabalho[], dataISO: string): boolean =>
    ausencias.some(a => a.data === dataISO && a.dia_inteiro);

/** Ausência parcial que cobre um minuto específico daquele dia (ausências de dia inteiro já tratadas à parte). */
const ausenciaParcialCobre = (ausencias: AusenciaTrabalho[], dataISO: string, minutos: number): boolean =>
    ausencias.some(a => {
        if (a.data !== dataISO || a.dia_inteiro) return false;
        if (!a.hora_inicio || !a.hora_fim) return false;
        return minutos >= horaParaMinutos(a.hora_inicio) && minutos < horaParaMinutos(a.hora_fim);
    });

/**
 * A pessoa está disponível pra receber um lead nesse instante? Considera
 * escala do dia da semana, almoço (pode variar por dia) e ausências.
 */
export function estaDisponivel(horarios: HorarioTrabalho[], ausencias: AusenciaTrabalho[], instante: Date): boolean {
    const { dow, minutos, dataISO } = paraLocalBRT(instante);
    const horario = horarios.find(h => h.dia_semana === dow);
    if (!horario || !horario.ativo) return false;

    const inicio = horaParaMinutos(horario.hora_inicio);
    const fim = horaParaMinutos(horario.hora_fim);
    if (minutos < inicio || minutos >= fim) return false;

    if (horario.almoco_inicio && horario.almoco_fim) {
        const almocoInicio = horaParaMinutos(horario.almoco_inicio);
        const almocoFim = horaParaMinutos(horario.almoco_fim);
        if (minutos >= almocoInicio && minutos < almocoFim) return false;
    }

    if (temAusenciaDiaInteiro(ausencias, dataISO)) return false;
    if (ausenciaParcialCobre(ausencias, dataISO, minutos)) return false;

    return true;
}

/** Subtrai [subInicio, subFim) de uma lista de intervalos [inicio, fim), em minutos. */
const subtrairIntervalo = (intervalos: Array<[number, number]>, subInicio: number, subFim: number): Array<[number, number]> => {
    const resultado: Array<[number, number]> = [];
    for (const [ini, fim] of intervalos) {
        if (subFim <= ini || subInicio >= fim) {
            resultado.push([ini, fim]);
            continue;
        }
        if (subInicio > ini) resultado.push([ini, Math.min(subInicio, fim)]);
        if (subFim < fim) resultado.push([Math.max(subFim, ini), fim]);
    }
    return resultado.filter(([i, f]) => f > i);
};

/** Intervalos de expediente de um dia específico (minutos desde meia-noite), já descontando almoço e ausências. */
const intervalosDisponiveisDoDia = (horario: HorarioTrabalho, ausencias: AusenciaTrabalho[], dataISO: string): Array<[number, number]> => {
    if (!horario.ativo || temAusenciaDiaInteiro(ausencias, dataISO)) return [];

    let intervalos: Array<[number, number]> = [[horaParaMinutos(horario.hora_inicio), horaParaMinutos(horario.hora_fim)]];

    if (horario.almoco_inicio && horario.almoco_fim) {
        intervalos = subtrairIntervalo(intervalos, horaParaMinutos(horario.almoco_inicio), horaParaMinutos(horario.almoco_fim));
    }

    for (const a of ausencias) {
        if (a.data !== dataISO || a.dia_inteiro || !a.hora_inicio || !a.hora_fim) continue;
        intervalos = subtrairIntervalo(intervalos, horaParaMinutos(a.hora_inicio), horaParaMinutos(a.hora_fim));
    }

    return intervalos;
};

/**
 * Tempo útil decorrido (ms) entre dois instantes, contando só o que cai dentro
 * do expediente da pessoa (equivalente por-pessoa do getBusinessElapsedMs de
 * src/lib/slaUtils.ts, que só exclui fim de semana pra empresa toda).
 */
export function tempoUtilDecorrido(horarios: HorarioTrabalho[], ausencias: AusenciaTrabalho[], inicio: Date, fim: Date): number {
    if (inicio.getTime() > fim.getTime()) return fim.getTime() - inicio.getTime(); // fallback negativo, mesma convenção de slaUtils.ts

    const brtInicioMs = inicio.getTime() - BRT_OFFSET_MS;
    const brtFimMs = fim.getTime() - BRT_OFFSET_MS;

    let cursor = brtInicioMs;
    let totalMs = 0;

    while (cursor < brtFimMs) {
        const cursorDate = new Date(cursor);
        const dow = cursorDate.getUTCDay();
        const dataISO = `${cursorDate.getUTCFullYear()}-${pad2(cursorDate.getUTCMonth() + 1)}-${pad2(cursorDate.getUTCDate())}`;

        const meiaNoiteDoDia = Date.UTC(cursorDate.getUTCFullYear(), cursorDate.getUTCMonth(), cursorDate.getUTCDate(), 0, 0, 0, 0);

        const proximaMeiaNoite = new Date(cursor);
        proximaMeiaNoite.setUTCDate(cursorDate.getUTCDate() + 1);
        proximaMeiaNoite.setUTCHours(0, 0, 0, 0);
        let proximaMeiaNoiteMs = proximaMeiaNoite.getTime();
        if (proximaMeiaNoiteMs > brtFimMs) proximaMeiaNoiteMs = brtFimMs;

        const horario = horarios.find(h => h.dia_semana === dow);
        if (horario) {
            for (const [iniMin, fimMin] of intervalosDisponiveisDoDia(horario, ausencias, dataISO)) {
                const iniMs = meiaNoiteDoDia + iniMin * MINUTO_MS;
                const fimMs = meiaNoiteDoDia + fimMin * MINUTO_MS;
                const sobreposI = Math.max(iniMs, cursor);
                const sobreposF = Math.min(fimMs, proximaMeiaNoiteMs);
                if (sobreposF > sobreposI) totalMs += (sobreposF - sobreposI);
            }
        }

        cursor = proximaMeiaNoiteMs;
    }

    return totalMs;
}
